/* PAGINA 1 · TUTTI I BACKTEST — la lista su 1.507.455 righe.
 *
 * Come regge quel numero: non lo regge il browser. `GET /backtester/search` è
 * l'unico endpoint del backend con paginazione e ordinamento veri, e questa
 * schermata non fa nient'altro che chiedergli una pagina per volta:
 *
 *  - il server seleziona SOLO le colonne leggere (niente `trades`,
 *    `equityLinePoints`, `candlesClosePrices`: pesano megabyte a riga), quindi
 *    una pagina da 200 righe resta nell'ordine delle decine di kilobyte;
 *  - l'ordinamento è una QUERY, non un riordino di pagina: si ordina su tutti i
 *    risultati, non sui 50 a schermo. Il controllo lo dichiara e la lista si
 *    blocca mentre il server interroga, perché lasciarla viva farebbe aprire la
 *    riga sbagliata quando l'elenco si sostituisce sotto il dito;
 *  - i criteri offerti sono solo quelli whitelistati dal server: fuori lista
 *    ricadrebbe su `strategyChange` IN SILENZIO e l'utente vedrebbe un
 *    ordinamento diverso da quello chiesto;
 *  - filtri, ordinamento e pagina vivono nell'URL, così una combinazione
 *    costata una query si può condividere e ritrovare senza ripagarla;
 *  - le richieste in volo vengono annullate (`AbortSignal` di `usaRichiesta`):
 *    cambiare tre volte criterio non lascia tre risposte che si sovrascrivono
 *    a caso.
 *
 * Nessuna virtualizzazione e nessuno scorrimento infinito: con un totale a
 * sette cifre lo scorrimento infinito non ha fondo, e la paginazione dichiarata
 * dice quanto costa ogni passo.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Avvisi,
  ControlloOrdinamento,
  Distintivo,
  Foglio,
  IconaFiltri,
  IntestazionePagina,
  Paginazione,
  Pastiglia,
  Pulsante,
  usaAvvisi,
  usaBotPerConfigurazione,
  type BotCollegato,
  type CriterioOrdinamento,
} from '../../componenti';
import { backtest } from '../../api/risorse';
import { usaRichiesta } from '../../api/usaRichiesta';
import { chiaveConfig, type ConfigurazioneConfrontabile } from '../../lib/chiaveConfig';
import { num, periodo as etichettaPeriodo, timeframe } from '../../lib/formato';
import { BarraConfronto } from './BarraConfronto';
import { ListaBacktest, type RigaBacktest } from './ListaBacktest';
import {
  commuta,
  indirizzoConfronto,
  leggiSelezione,
  preserva,
} from './statoConfronto';
import { AzioniFiltri, PannelloFiltri, usaFiltriApplicati } from './PannelloFiltri';
import {
  DIMENSIONI_PAGINA,
  azzeraFiltri,
  filtriAttivi,
  leggiDaUrl,
  rimuoviFiltro,
  scriviInUrl,
  type ChiaveFiltro,
  type StatoRicerca,
} from './statoRicerca';
import './backtest.css';

/* I criteri offerti sono ESATTAMENTE quelli che il server sa ordinare. Uno in
   più verrebbe ignorato senza un messaggio: la lista tornerebbe ordinata per
   profitto e nessuno saprebbe perché. */
const CRITERI: CriterioOrdinamento[] = [
  { chiave: 'strategyChange', etichetta: 'Profitto', versoNaturale: 'DESC' },
  { chiave: 'drawdownMaxValue', etichetta: 'Drawdown', versoNaturale: 'ASC' },
  { chiave: 'walkForwardScore', etichetta: 'Tenuta', versoNaturale: 'DESC' },
  { chiave: 'bearYearsAvg', etichetta: 'Nei ribassi', versoNaturale: 'DESC' },
  { chiave: 'intorno', etichetta: 'Intorno', versoNaturale: 'DESC' },
  { chiave: 'cagr', etichetta: 'CAGR', versoNaturale: 'DESC' },
  { chiave: 'calmar', etichetta: 'Calmar', versoNaturale: 'DESC' },
  { chiave: 'sharpe', etichetta: 'Sharpe', versoNaturale: 'DESC' },
  { chiave: 'tradesNumber', etichetta: 'Operazioni', versoNaturale: 'DESC' },
];

/** Come si chiama a video ogni filtro, per pastiglie e stato vuoto. */
const NOMI_FILTRO: Record<ChiaveFiltro, string> = {
  strategyName: 'STRATEGIA',
  asset: 'ASSET',
  candleSize: 'TIMEFRAME',
  backtestGroupId: 'GRUPPO',
  startDate: 'COPRE DAL',
  endDate: 'COPRE FINO AL',
  minTrades: 'OPS MIN',
  minChange: 'PROFITTO MIN',
  maxDrawdown: 'DD MAX',
  minIntorno: 'INTORNO MIN',
  minTenuta: 'TENUTA MIN',
};

function valoreFiltro(s: StatoRicerca, k: ChiaveFiltro): string {
  const v = s[k];
  if (v == null) return '';
  if (k === 'candleSize') return timeframe(Number(v));
  if (k === 'backtestGroupId') return `#${v}`;
  if (k === 'minChange' || k === 'maxDrawdown') return `${v}%`;
  if (k === 'minTrades') return num(Number(v));
  return String(v);
}

/**
 * I tradebot che girano una configurazione, cercati nell'indice.
 *
 * `null` finche' l'indice non e' letto: scrivere «nessun bot» prima di aver
 * guardato e' una conclusione non guadagnata, e su una riga che invece gira con
 * denaro vero e' la piu' costosa che questa lista possa dire.
 */
function botDellaRiga(
  indice: Map<string, BotCollegato[]> | null,
  riga: ConfigurazioneConfrontabile,
): BotCollegato[] | null {
  if (indice == null) return null;
  const k = chiaveConfig(riga);
  /* Un backtest senza `strategyParams` non e' confrontabile con niente: nessun
     bot puo' corrispondergli, e questo si sa senza guardare l'indice. */
  if (k == null) return [];
  return indice.get(k) ?? [];
}

export function TuttiIBacktest() {
  const navigate = useNavigate();
  const [parametri, setParametri] = useSearchParams();
  const [foglioAperto, setFoglioAperto] = useState(false);
  const { avvisi, mostra } = usaAvvisi();

  const stato = useMemo(() => leggiDaUrl(parametri), [parametri]);

  /* La selezione da confrontare vive nell'URL come i filtri, ma in un parametro
     suo: non e' un filtro e non cambia la query. Sta fuori da `StatoRicerca`
     perche' quel modello dichiara di corrispondere uno a uno ai parametri che il
     server accetta. */
  const selezione = useMemo(() => leggiSelezione(parametri), [parametri]);

  /* `replace` per la sola paginazione avrebbe risparmiato voci nella cronologia,
     ma il tasto indietro dopo aver girato dieci pagine deve riportare alla
     pagina precedente, non alla schermata precedente.

     `preserva` non e' un dettaglio: `scriviInUrl` costruisce l'indirizzo da zero
     con i soli campi della ricerca, quindi senza di lui la selezione si
     cancellerebbe al primo filtro toccato -- e una selezione costata la fatica
     di trovare due righe fra un milione e mezzo non puo' sparire perche' si e'
     cambiata pagina. */
  const vaiA = useCallback(
    (nuovo: StatoRicerca) => setParametri(preserva(scriviInUrl(nuovo), selezione)),
    [setParametri, selezione],
  );

  /* Il tocco sulla casella. Quando il confronto e' pieno il tocco NON viene
     ignorato: la selezione resta com'e' e la ragione si scrive a schermo. */
  const commutaSelezione = useCallback(
    (id: number) => {
      const esito = commuta(selezione, id);
      if (esito.rifiutato) {
        mostra(esito.rifiutato, 'fallito');
        return;
      }
      setParametri(preserva(scriviInUrl(stato), esito.ids));
    },
    [selezione, stato, setParametri, mostra],
  );

  /* --- le faccette: valori distinti per i filtri. Una sola volta, non a ogni
     query: sono cinque GROUP BY su tutta la tabella. --- */
  const faccette = usaRichiesta((segnale) => backtest.faccette(segnale), [], {
    eVuoto: () => false,
  });

  /* --- la ricerca vera. Le dipendenze sono i singoli campi e non l'oggetto:
     un oggetto nuovo a ogni render rilancerebbe la query di continuo. --- */
  const ricerca = usaRichiesta(
    (segnale) =>
      backtest.cerca(
        {
          page: stato.page,
          limit: stato.limit,
          strategyName: stato.strategyName ?? undefined,
          asset: stato.asset ?? undefined,
          candleSize: stato.candleSize ?? undefined,
          backtestGroupId: stato.backtestGroupId ?? undefined,
          startDate: stato.startDate ?? undefined,
          endDate: stato.endDate ?? undefined,
          minTrades: stato.minTrades ?? undefined,
          minChange: stato.minChange ?? undefined,
          maxDrawdown: stato.maxDrawdown ?? undefined,
          minIntorno: stato.minIntorno ?? undefined,
          // Il server accetta piu' criteri separati da virgola, e i versi nella stessa forma.
          sortBy: stato.ordine.map((o) => o.chiave).join(','),
          sortDir: stato.ordine.map((o) => o.verso).join(','),
          minTenuta: stato.minTenuta ?? undefined,
        },
        segnale,
      ),
    [
      stato.page,
      stato.limit,
      stato.ordine,
      stato.minTenuta,
      stato.strategyName,
      stato.asset,
      stato.candleSize,
      stato.backtestGroupId,
      stato.startDate,
      stato.endDate,
      stato.minTrades,
      stato.minChange,
      stato.maxDrawdown,
      stato.minIntorno,
    ],
  );

  /* --- quali di queste configurazioni sono GIA' un tradebot. I bot sono una
     dozzina: si scaricano una volta e si indicizzano per chiave, cosi' la lista
     non fa una chiamata per riga. E' una richiesta a parte perche' non deve
     ritardare i risultati: le righe compaiono subito e il distintivo si accende
     quando l'indice arriva. --- */
  const bot = usaBotPerConfigurazione();

  const risposta = ricerca.dati;
  const totale = risposta?.total ?? null;
  const pagineTotali = risposta?.pageCount ?? 1;
  const inCorso = ricerca.stato === 'caricamento' || ricerca.inAggiornamento;

  const righe: RigaBacktest[] = useMemo(
    () =>
      (risposta?.data ?? []).map((b) => ({
        id: b.id,
        strategia: b.strategyName,
        asset: b.asset,
        currency: b.currency,
        candleSize: b.candleSize,
        gruppo: b.backtestGroupId,
        profitto: b.strategyChange,
        drawdown: b.drawdownMaxValue,
        operazioni: b.tradesNumber,
        /* Regola 2: il periodo viaggia con la riga, non con la pagina. Righe
           diverse possono coprire periodi diversi anche nella stessa lista. */
        periodo: etichettaPeriodo(b.startDate, b.endDate),
        tenuta: b.walkForwardScore,
        tenutaMetodo: b.walkForwardMetodo,
        ribassi: b.bearYearsAvg,
        anniNegativi: b.bearYearsCount,
        calmar: b.calmar,
        preferito: b.isFavorite,
        anni: b.yearlyChange ?? null,
        /* `null` finche' l'indice non c'e': una mappa non ancora letta direbbe
           «nessun bot» su una riga che invece ne ha uno con denaro vero. */
        bot: botDellaRiga(bot.indice, b),
      })),
    [risposta, bot.indice],
  );

  const attivi = filtriAttivi(stato);

  /* Uno stato vuoto che dice solo "nessun risultato" costringe a indovinare
     quale dei dieci filtri ha tolto tutto. Qui li elenca, e propone di
     allentare quello che di solito è il più stretto. */
  const causaVuoto =
    attivi.length === 0
      ? 'Il magazzino dei backtest non ha restituito righe: non è un filtro, è la tabella a essere vuota o irraggiungibile.'
      : `Nessun backtest soddisfa questi ${attivi.length} vincoli insieme: ${attivi
          .map((k) => `${NOMI_FILTRO[k].toLowerCase()} ${valoreFiltro(stato, k)}`)
          .join(' · ')}.`;

  /* Allentare il vincolo di solito più stretto costa un tocco solo: azzerare
     tutto butterebbe via anche i filtri che si volevano tenere. */
  const ddPiuLargo = stato.maxDrawdown == null ? null : Math.round(stato.maxDrawdown) + 10;
  const azioneVuoto =
    ddPiuLargo != null
      ? {
          etichetta: `Porta il drawdown massimo a ${ddPiuLargo}%`,
          onClick: () => vaiA({ ...stato, maxDrawdown: ddPiuLargo, page: 1 }),
        }
      : attivi.length > 0
        ? { etichetta: 'Azzera i filtri', onClick: () => vaiA(azzeraFiltri(stato)) }
        : undefined;

  const contesto = faccette.dati
    ? `${num(faccette.dati.gruppi.length)} gruppi · ${num(faccette.dati.strategie.length)} strategie · ${num(faccette.dati.assets.length)} asset`
    : undefined;

  /* I filtri si applicano da soli: nessun "Applica". `vaiA` scrive nell'URL, e
     l'URL è ciò che fa partire la query — quindi passare `vaiA` all'hook è
     letteralmente dargli il permesso di interrogare.

     Una sola istanza per le due copie del pannello: la barra laterale e il
     foglio sono entrambi montati, e due stati indipendenti si scambierebbero i
     filtri sotto le dita al primo cambio di larghezza. */
  const filtri = usaFiltriApplicati(stato, vaiA);

  /* `chiudi` solo nel foglio: senza "Applica" il piede resterebbe con il solo
     azzeramento, e chi ha finito di filtrare non avrebbe un modo evidente di
     tornare alla lista. */
  const azioniFiltri = (chiudi?: () => void) => (
    <AzioniFiltri
      bozza={filtri.bozza}
      onChiudi={chiudi}
      onAzzera={() => {
        const pulito = azzeraFiltri(filtri.bozza);
        filtri.imposta(pulito);
        vaiA(pulito);
      }}
    />
  );

  return (
    <div className="sb-cornice">
      <Avvisi avvisi={avvisi} />

      <IntestazionePagina
        titolo="Tutti i backtest"
        sotto={
          <>
            <Distintivo tono="accento">
              {totale == null ? '—' : num(totale)} risultati
            </Distintivo>
            {contesto && <span className="sb-nota">{contesto}</span>}
          </>
        }
        azioni={
          <>
            {/* Il pulsante dei filtri esiste solo dove i filtri sono un foglio:
                sul desktop la barra laterale è già a schermo. La scelta è del CSS. */}
            <span className="sb-fino-a-tablet">
              <Pulsante onClick={() => setFoglioAperto(true)}>
                <IconaFiltri />
                Filtri
                {attivi.length > 0 && <Distintivo tono="accento">{attivi.length}</Distintivo>}
              </Pulsante>
            </span>
            {/* Da qui si CREA. Prima mancava del tutto: si potevano consultare
                un milione e mezzo di righe senza poterne aggiungere una. */}
            <Pulsante variante="primario" onClick={() => navigate('/backtest/nuovo')}>
              Nuovo backtest
            </Pulsante>
          </>
        }
      />

      {attivi.length > 0 && (
        <div className="sb-bt-pastiglie" style={{ padding: '0 var(--sb-s-8) var(--sb-s-6)' }}>
          {attivi.map((k) => (
            <Pastiglia
              key={k}
              chiave={NOMI_FILTRO[k]}
              valore={valoreFiltro(stato, k)}
              onRimuovi={() => vaiA(rimuoviFiltro(stato, k))}
            />
          ))}
        </div>
      )}

      <div className="sb-corpo-con-filtri">
        {/* barra laterale: solo da 1100px */}
        <div className="sb-corpo-con-filtri__filtri sb-solo-desktop">
          <PannelloFiltri
            bozza={filtri.bozza}
            onScegli={filtri.applicaSubito}
            onDigita={filtri.applicaDopoLaPausa}
            faccette={faccette.dati}
            statoFaccette={faccette.stato}
            azioni={azioniFiltri()}
          />
        </div>

        <div className="sb-corpo-con-filtri__contenuto">
          <ControlloOrdinamento
            criteri={CRITERI}
            ordine={stato.ordine}
            totale={totale}
            inCorso={inCorso}
            contesto={contesto}
            onCambia={(ordine) =>
              /* Ordinare cambia l'insieme, non l'ordine dei 50 a schermo:
                 restare a pagina 12 mostrerebbe righe che non c'entrano più. */
              vaiA({ ...stato, ordine, page: 1 })
            }
          />

          <BarraConfronto
            selezionati={selezione}
            righe={righe}
            onTogli={commutaSelezione}
            onSvuota={() => setParametri(scriviInUrl(stato))}
            onConfronta={() => navigate(indirizzoConfronto(selezione))}
          />

          <ListaBacktest
            righe={righe}
            selezionati={selezione}
            onSeleziona={commutaSelezione}
            stato={ricerca.stato}
            causaVuoto={causaVuoto}
            azioneVuoto={azioneVuoto}
            messaggioErrore={ricerca.errore?.message}
            onRiprova={ricerca.ricarica}
            onApri={(id) => navigate(`/backtest/${id}`)}
            interrogazioneInCorso={
              ricerca.inAggiornamento
                ? `interrogo ${totale == null ? 'tutti i' : num(totale)} risultati…`
                : null
            }
          />

          <div
            className="sb-riga"
            style={{ padding: 'var(--sb-s-5) var(--sb-s-7)', borderTop: '1px solid var(--sb-bordo-tenue)' }}
          >
            <div className="sb-bt-dimensioni">
              <span className="sb-nota">Per pagina</span>
              <div className="sb-bt-dimensioni__gruppo">
                {DIMENSIONI_PAGINA.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`sb-bt-dimensione${stato.limit === n ? ' sb-bt-dimensione--scelta' : ''}`}
                    aria-pressed={stato.limit === n}
                    disabled={inCorso}
                    onClick={() => vaiA({ ...stato, limit: n, page: 1 })}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <span className="sb-spazio" />
            <span className="sb-nota">
              {stato.limit} righe per pagina: oltre 200 il server taglia comunque.
            </span>
          </div>

          <Paginazione
            pagina={stato.page}
            pagineTotali={pagineTotali}
            perPagina={stato.limit}
            totale={totale}
            inCorso={inCorso}
            onVai={(p) => vaiA({ ...stato, page: p })}
          />

          {/* La stessa barra, di nuovo: chi ha spuntato la seconda casella in
              fondo alla pagina deve trovare il pulsante li', non in cima. */}
          <BarraConfronto
            selezionati={selezione}
            righe={righe}
            onTogli={commutaSelezione}
            onSvuota={() => setParametri(scriviInUrl(stato))}
            onConfronta={() => navigate(indirizzoConfronto(selezione))}
          />
        </div>
      </div>

      {/* foglio dal basso: telefono e tablet. La conferma sta nel piede, che è
          appiccicato in fondo: dopo dieci filtri il pollice la trova comunque. */}
      <Foglio
        aperta={foglioAperto}
        onChiudi={() => setFoglioAperto(false)}
        titolo="Filtri"
        /* «su 0 risultati» era falso quando il totale non era ancora arrivato:
           un conteggio non letto non è un conteggio a zero. Le altre tre
           occorrenze di `totale` in questo file lo trattano già così. */
        sottotitolo={
          totale == null
            ? `${attivi.length} applicati`
            : `${attivi.length} applicati su ${num(totale)} risultati`
        }
        pieda={azioniFiltri(() => setFoglioAperto(false))}
      >
        <PannelloFiltri
          bozza={filtri.bozza}
          onScegli={filtri.applicaSubito}
          onDigita={filtri.applicaDopoLaPausa}
          faccette={faccette.dati}
          statoFaccette={faccette.stato}
        />
      </Foglio>
    </div>
  );
}
